// angular dependencies
import { HttpClient, HttpResponseBase } from '@angular/common/http';
import { Injectable, Signal, inject } from '@angular/core';
// fantaz dependencies
import { toSignal } from '@angular/core/rxjs-interop';

// third-party dependencies
import { Amplify } from 'aws-amplify';
import {
  SetUpTOTPOutput,
  UpdateMFAPreferenceInput,
  VerifyTOTPSetupInput,
  autoSignIn,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  confirmUserAttribute,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  rememberDevice,
  resendSignUpCode,
  resetPassword,
  sendUserAttributeVerificationCode,
  setUpTOTP,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updateMFAPreference,
  updatePassword,
  updateUserAttribute,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { IAuthDevice } from 'libs/auth/data-access/src/lib/models/cognito';
import { merge } from 'lodash';
import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { UAParser } from 'ua-parser-js';

import { TrackError } from '@fantaz/core/utils';
import { environment } from '@fantaz/shared/app-config';
import {
  AccountExistFailure,
  AuthSession,
  ConfirmResetPasswordInput,
  ConfirmSignInInput,
  ConfirmSignInOutput,
  ConfirmSignUpInput,
  ConfirmSignUpOutput,
  ConfirmUserAttributeInput,
  CurrentUser,
  DuplicateEmailFailure,
  DuplicatePhoneNumberFailure,
  DuplicateUsernameFailure,
  FetchAuthSessionOptions,
  FetchMFAPreferenceOutput,
  InvalidEmailFailure,
  InvalidPhoneNumberFailure,
  ResendSignUpCodeInput,
  ResendSignUpCodeOutput,
  ResetPasswordInput,
  ResetPasswordOutput,
  SendUserAttributeVerificationCodeInput,
  SendUserAttributeVerificationCodeOutput,
  SignInInput,
  SignInOutput,
  SignInWithMagicLinkInput,
  SignInWithRedirectInput,
  SignUpInput,
  SignUpOutput,
  SignUpUserUnconfirmedFailure,
  SignUpValidationFailure,
  UpdatePasswordInput,
  UpdateUserAttributeInput,
  UpdateUserAttributeOutput,
} from '@fantaz/shared/data-access/models';
import { differenceInCalendarDays } from '@fantaz/shared/utils/date';
import {
  formatPhoneNumberForSubmit,
  isValidUSPhoneNumber,
} from '@fantaz/shared/utils/forms';

import { FzAuthFailures } from './auth-service.failures';
import { AuthService, CheckUsernameResult } from './auth-service.interface';

// interfaces only for this service
type CoreClientApiUserExistsEmailResult = {
  exists: boolean;
  provider: string;
  emailVerified: boolean;
  phoneType: 'INVALID' | 'VALID';
  validPhoneNumber: boolean;
};

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolClientId: environment.Auth.userPoolWebClientId as string,
      userPoolId: environment.Auth.userPoolId as string,
      identityPoolId: environment.Auth.identityPoolId as string,
      mfa: {
        status: 'optional',
      },
      loginWith: {
        oauth: {
          domain: environment.Auth.oauth?.domain as string,
          redirectSignIn: [environment.Auth.oauth?.redirectSignIn] as any,
          redirectSignOut: [environment.Auth.oauth?.redirectSignOut] as any,
          responseType: environment.Auth.oauth?.responseType as any,
          scopes: environment.Auth.oauth?.scope as any,
        },
        username: true,
        email: true,
        phone: true,
      },
    },
  },
});

const KEYS_TO_REMOVE = [
  'FzCognitoKeys',
  'access_token',
  'id_token',
  'refresh_token',
  'user_id',
  'user_session',
  'username',
  'expires_at',
  'zboExpiration',
  'prefs',
];

@Injectable()
export class AuthCognitoServiceImpl implements AuthService {
  persistentKey = [
    'sponsorName',
    'redirectTo',
    'promo-code',
    'home_video_view',
    'user_device_key',
  ];
  persistentPartialKeys = [
    'deviceKey',
    'deviceGroupKey',
    'randomPasswordKey',
    'rememberedDevice',
  ];
  LOCAL_STORAGE_ID_TOKEN = 'id_token';
  LOCAL_STORAGE_EXPIRES_AT = 'expires_at';
  LOCAL_STORAGE_USER_SESSION = 'user_session';
  LOCAL_STORAGE_USER_ID = 'user_id';
  LOCAL_STORAGE_USERNAME = 'username';

  /**
   * HttpClient
   */
  readonly #httpClient = inject(HttpClient);
  invite: string;
  loginDialogRef: any;

  saveIntoLocalStorage(session: AuthSession): void {
    if (session.tokens) {
      const { tokens } = session;
      // Store the user session into the local storage
      localStorage.setItem(
        this.LOCAL_STORAGE_USER_SESSION,
        JSON.stringify(session),
      );

      localStorage.setItem(
        this.LOCAL_STORAGE_EXPIRES_AT,
        tokens?.accessToken?.payload.exp + '',
      );
      const id =
        tokens?.idToken?.payload['custom:fantaz_uuid'] ||
        tokens?.idToken?.payload['fantaz_uuid'] ||
        tokens?.idToken?.payload?.sub ||
        '';

      localStorage.setItem(this.LOCAL_STORAGE_USER_ID, id + '');
      const idToken = session.tokens?.idToken?.toString() || '';
      localStorage.setItem(this.LOCAL_STORAGE_ID_TOKEN, idToken);
      localStorage.setItem(
        this.LOCAL_STORAGE_USERNAME,
        tokens?.idToken?.payload['preferred_username'] + '' || '',
      );
    }
  }
  resendAddContactPointVerificationCode(
    data: SendUserAttributeVerificationCodeInput,
  ): Observable<SendUserAttributeVerificationCodeOutput> {
    throw new Error('Method not implemented.');
  }

  onAuthStateChanged(callback: any) {
    Hub.listen('auth', callback);
  }

  /**
   * Sign in the user with the username and password. It will return the
   * session of the user.
   */
  signIn(payload: SignInInput): Observable<SignInOutput> {
    return from(
      signIn({
        ...payload,
      }),
    ).pipe(
      catchError((error) => {
        return this.handleAuthError(error);
      }),
    );
  }

  canISignIn(provider: string): Observable<boolean> {
    const ua = new UAParser();
    const browser = ua.getBrowser().name?.toLowerCase();
    const platform = ua.getOS().name?.toLowerCase();

    if (provider === 'Google') {
      /*
       * Create a matrix array that contains the browser and platform
       * compatibility for the Google provider.
       * https://developers.google.com/identity/gsi/web/guides/supported-browsers
       */
      const browsers = ['chrome', 'edge', 'firefox', 'safari', 'mobile safari'];
      const platforms = ['android', 'ios', 'mac os', 'linux', 'windows'];
      const matrix: boolean[][] = [
        [true, true, true, true, true],
        [true, true, true, true, true],
        [true, true, true, true, true],
        [false, true, true, false, false],
        [false, true, true, false, false],
      ];

      if (browser && platform) {
        const bi = browsers.indexOf(browser);
        const pi = platforms.indexOf(platform);

        if (
          bi >= 0 &&
          bi <= matrix.length &&
          pi >= 0 &&
          pi <= matrix[bi].length
        ) {
          return of(matrix[bi][pi]);
        }
      }

      TrackError.trackCustomError(
        `can-i-sign-in-failure`,
        `Browser "${browser}" and platform "${platform}" are not supported for Google Sign-In. ${JSON.stringify(
          {
            userAgent: navigator.userAgent,
          },
        )}`,
      );

      return of(false);
    }

    return of(true);
  }

  sendMagicLink(payload: SignInInput): Observable<ConfirmSignInOutput> {
    return from(
      this.signIn({
        ...payload,
        options: {
          ...payload.options,
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          clientMetadata: {
            ...payload.options?.clientMetadata,
          },
        },
      }),
    ).pipe(
      switchMap(() => {
        return this.confirmSignIn({
          challengeResponse: '__dummy__',
          options: {
            clientMetadata: {
              ...payload.options?.clientMetadata,
              signInMethod: 'MAGIC_LINK',
              alreadyHaveMagicLink: 'no',
            },
          },
        });
      }),
      catchError((error) => {
        return this.handleAuthError(error);
      }),
    );
  }

  singInWithMagicLink(
    data: SignInWithMagicLinkInput,
  ): Observable<SignInOutput> {
    return from(
      this.signIn({
        username: data.username,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          clientMetadata: {
            signInMethod: 'MAGIC_LINK',
          },
        },
      }),
    ).pipe(
      switchMap(() => {
        return this.confirmSignIn({
          challengeResponse: data.challengeResponse as string,
          options: {
            clientMetadata: {
              signInMethod: 'MAGIC_LINK',
              alreadyHaveMagicLink: 'yes',
              ...data.clientMetadata,
            },
          },
        });
      }),
      catchError((error) => {
        return this.handleAuthError(error);
      }),
    );
  }

  /**
   * Fetch the MFA preference of the user.
   */
  fetchMFAPreference(): Observable<FetchMFAPreferenceOutput> {
    return from(fetchMFAPreference());
  }

  /**
   * Confirm the sign in of the user.
   */
  confirmSignIn(data: ConfirmSignInInput): Observable<ConfirmSignInOutput> {
    return from(
      confirmSignIn({
        ...data,
      }),
    );
  }

  /**
   * Sign in the user with the external provider. It will redirect the user to
   * the external provider login page.
   */
  signInWithExternalProvider(
    data: SignInWithRedirectInput,
  ): Observable<boolean> {
    return from(
      signInWithRedirect({
        provider: data.provider,
        customState: data.customState,
        options: data.options,
      }),
    ).pipe(
      map((result) => {
        return true;
      }),
    );
  }
  /**
   * Sign up the user with the username and password. It will return the
   * session of the user after sign up is confirmed.
   */
  signUp(data: SignUpInput): Observable<SignUpOutput> {
    if (data.options?.userAttributes.email) {
      data = merge(data, {
        options: {
          userAttributes: {
            email: data.options?.userAttributes.email.toLocaleLowerCase(),
          },
        },
      });
    }

    return from(
      signUp({
        ...data,
      }),
    ).pipe(
      catchError((error) => {
        if (error.message.includes('UserExists')) {
          return throwError(
            () => new AccountExistFailure(new Error('User already exists')),
          );
        }

        if (error.message.includes('human-validation-required')) {
          return throwError(
            () =>
              new SignUpValidationFailure(
                new Error('Sign up verification failed'),
              ),
          );
        }

        if (error.message.includes('UserUnconfirmed')) {
          return throwError(
            () => new SignUpUserUnconfirmedFailure(new Error(error)),
          );
        }

        return throwError(() => error);
      }),
    );
  }

  /**
   * Confirm the sign up of the user.
   */
  confirmSignUp(data: ConfirmSignUpInput): Observable<ConfirmSignUpOutput> {
    return from(
      confirmSignUp({
        confirmationCode: data.confirmationCode,
        username: data.username,
      }),
    ).pipe(
      switchMap((response) => {
        if (response.nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
          return from(autoSignIn()).pipe(map(() => response));
        }

        return of(response);
      }),
    );
  }

  /**
   * Resend the confirmation code while signing up
   */
  resendSignUpCode(
    data: ResendSignUpCodeInput,
  ): Observable<ResendSignUpCodeOutput> {
    return from(
      resendSignUpCode({
        username: data.username,
        options: {
          ...data.options,
        },
      }),
    );
  }

  /**
   * Remember the device of the user.
   */
  rememberDevice(): Observable<void> {
    return from(rememberDevice()).pipe(
      tap(() => {
        const keys = this.getMetadataDevice();
        if (keys) {
          const rememberedDeviceKey: string = `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${keys.username}.${keys.deviceKey}.rememberedDevice`;
          const dateTime = Date.now();
          localStorage.setItem(rememberedDeviceKey, dateTime + '');
        }
      }),
    );
  }

  private getMetadataDevice() {
    const username = localStorage.getItem(
      `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.LastAuthUser`,
    );
    if (username) {
      const deviceKey: string =
        localStorage.getItem(
          `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${username}.deviceKey`,
        ) || '';
      return { username, deviceKey };
    }
    return null;
  }

  /**
   * Load the current user from cognito and them return true if the user exist.
   * In case of error, return false.
   */
  isLoggedIn(): Observable<boolean> {
    return from(getCurrentUser()).pipe(
      map(() => true),
      catchError(() => {
        return of(false);
      }),
    );
  }

  isUserLoggedIn(): Signal<boolean> {
    return toSignal(this.isLoggedIn(), {
      initialValue: false,
    });
  }

  /**
   * Get the current session from cognito. The session is a object with the
   * tokens and the user data. Not return the user data from the api.
   */
  getAuthSession(data?: FetchAuthSessionOptions): Observable<AuthSession> {
    return from(fetchAuthSession({ forceRefresh: data?.forceRefresh }));
  }

  /**
   * Get the current user from the api.
   */
  getCurrentUser(): Observable<CurrentUser> {
    return this.#httpClient
      .get<CurrentUser>(
        `${environment.apiBaseUrl}/core-client/api/user/currentUser`,
      )
      .pipe(
        map((currentUser) => ({
          ...currentUser,
        })),
      );
  }

  /**
   * Refresh the current session.
   */
  refreshSession(): Observable<AuthSession> {
    return from(fetchAuthSession({ forceRefresh: true })).pipe(
      tap((session) => {
        this.saveIntoLocalStorage(session);
      }),
    );
  }

  /**
   * Reset the password of the user.  It will send a code to the user email or phone number and then
   * the user can reset the password.
   */
  resetPassword(data: ResetPasswordInput): Observable<ResetPasswordOutput> {
    return from(
      resetPassword({
        ...data,
      }),
    );
  }

  /**
   * Confirms the new password and verification code to reset the password.
   *
   * @param input -  The ConfirmResetPasswordInput object.
   * @throws -{@link ConfirmForgotPasswordException }
   * Thrown due to an invalid confirmation code or password.
   * @throws -{@link AuthValidationErrorCode }
   * Thrown due to an empty confirmation code, password or username.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  confirmResetPassword(data: ConfirmResetPasswordInput): Observable<void> {
    return from(
      confirmResetPassword({
        ...data,
      }),
    );
  }

  /**
   * Update the password of the user.
   */
  updatePassword(data: UpdatePasswordInput): Observable<void> {
    return from(
      updatePassword({
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      }),
    );
  }

  /**
   * Logout the user from the application. It will remove the cognito session
   * from the local storage. In case of global logout, it will remove all
   * sessions from the all devices.
   */
  signOut(global: boolean = false): Observable<boolean> {
    return from(
      signOut({ global }).then(() => {
        this.clearAuthState();
        return true;
      }),
    );
  }

  clearAuthState(): void {
    this.checkRememberedDevice();
    // remove cognito session from local storage
    Object.keys(localStorage).forEach((key) => {
      if (KEYS_TO_REMOVE.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // get class properties and remove them from local storage
    Object.keys(this).forEach((key) => {
      if (
        key.startsWith('LOCAL_STORAGE_') &&
        !this.persistentKey.includes(key)
      ) {
        localStorage.removeItem(this[key as keyof this] as string);
      }
    });

    // remove business object expiration from local storage
    Object.keys(localStorage).forEach((key) => {
      if (
        !this.persistentKey.includes(key) &&
        !this.validatePersistentKey(key)
      ) {
        localStorage.removeItem(key);
      }
    });
  }

  checkRememberedDevice() {
    const keys = this.getMetadataDevice();
    if (keys) {
      const rememberedDeviceKey: string = `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${keys.username}.${keys.deviceKey}.rememberedDevice`;
      const dateTimeStr = localStorage.getItem(rememberedDeviceKey);
      let days = undefined;
      if (dateTimeStr) {
        const dateTime = Number(dateTimeStr);
        const date = new Date(dateTime);
        days = differenceInCalendarDays(new Date(), date);
      }
      if (days === undefined || days >= 30) {
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${keys.username}.deviceKey`,
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${keys.username}.deviceGroupKey`,
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${keys.username}.randomPasswordKey`,
        );
        localStorage.removeItem(rememberedDeviceKey);
      }
    }
  }

  private validatePersistentKey(key: string): boolean {
    for (let i = 0; i < this.persistentKey.length; i++) {
      const element = this.persistentPartialKeys[i];
      if (key.includes(element)) {
        return true;
      }
    }
    return false;
  }

  handleAuthError(error: any): Observable<any> {
    if (
      error.message === 'Incorrect username or password.' ||
      error.code === 'UserNotFoundException'
    ) {
      return throwError(() => FzAuthFailures.invalidCredentials());
    }
    if (error.message === 'Password attempts exceeded') {
      return throwError(() => FzAuthFailures.maxAttempts());
    }
    return throwError(() => error);
  }

  /**
   * Add contact point to the user.
   */
  updateUserAttribute(
    data: UpdateUserAttributeInput,
    opts?: { custom?: boolean },
  ): Observable<UpdateUserAttributeOutput> {
    // format phone number
    if (data.userAttribute.attributeKey === 'phone_number') {
      data.userAttribute.value = formatPhoneNumberForSubmit(
        data.userAttribute.value,
      );
    }

    if (opts?.custom === true) {
      return this.#httpClient
        .post<UpdateUserAttributeOutput>(
          `${environment.apiBaseUrl}/core-client/api/user/verify-contact`,
          {
            attributeKey: data.userAttribute.attributeKey,
            value: data.userAttribute.value,
          },
        )
        .pipe(
          map(
            () =>
              ({
                isUpdated: true,
                nextStep: {
                  updateAttributeStep: 'DONE',
                  userAttribute: data.userAttribute,
                },
              }) as UpdateUserAttributeOutput,
          ),
        );
    }

    return from(
      updateUserAttribute({
        userAttribute: data.userAttribute,
        options: {
          clientMetadata: {
            [data.userAttribute.attributeKey]: data.userAttribute.value,
          },
        },
      }),
    );
  }

  /**
   * Resend a verification code to the new contact point.
   */
  sendUserAttributeVerificationCode(
    data: SendUserAttributeVerificationCodeInput,
  ): Observable<SendUserAttributeVerificationCodeOutput> {
    return from(
      sendUserAttributeVerificationCode({
        ...data,
      }),
    );
  }

  /**
   * Verify the code sent to the new contact point.
   */
  confirmUserAttribute(
    data: ConfirmUserAttributeInput,
    opts?: { custom: boolean },
  ): Observable<void> {
    if (opts?.custom === true) {
      return this.#httpClient
        .post<{
          message: string;
        }>(`${environment.apiBaseUrl}/core-client/api/user/confirm-contact`, {
          attributeKey: data.userAttributeKey,
          code: data.confirmationCode,
        })
        .pipe(
          map((res) => {
            if (res.message !== 'Success') {
              throw { name: 'ExceptionCodeMismatch', message: 'Invalid code' };
            }
          }),
        );
    }
    return from(
      confirmUserAttribute({
        userAttributeKey: data.userAttributeKey,
        confirmationCode: data.confirmationCode,
      }),
    );
  }

  getUserDevices(): Observable<IAuthDevice[]> {
    throw new Error('Method not implemented.');
  }

  updateMFAPreference(arg: UpdateMFAPreferenceInput): Observable<void> {
    return from(updateMFAPreference(arg));
  }
  setupTOTP(): Observable<SetUpTOTPOutput> {
    return from(setUpTOTP());
  }

  verifyTOTPSetup(args: VerifyTOTPSetupInput): Observable<void> {
    return from(verifyTOTPSetup(args));
  }

  getUserId(): string {
    const str = localStorage.getItem(this.LOCAL_STORAGE_USER_ID);

    return str ? str : '';
  }

  getUserIdToken(): string {
    const username = localStorage.getItem(
      `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.LastAuthUser`,
    );
    const path = `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${username}.idToken`;
    const str = localStorage.getItem(path);

    return str ? str : '';
  }

  getUserName(): string {
    return localStorage.getItem(this.LOCAL_STORAGE_USERNAME) || '';
  }

  /**
   * @deprecated
   */
  currentAuthenticatedUser(): Observable<any> {
    throw new Error('Method not implemented. or deprecated');
  }

  checkUsername(data: {
    attr: string;
    value: string;
  }): Observable<CheckUsernameResult> {
    // validate email if it is an email
    if (
      data.attr === 'email' &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.value)
    ) {
      return throwError(() => new InvalidEmailFailure());
    }

    if (data.attr === 'phone_number' && !isValidUSPhoneNumber(data.value)) {
      return throwError(() => new InvalidPhoneNumberFailure());
    }

    if (data.attr === 'phone_number') {
      data.value = formatPhoneNumberForSubmit(data.value);
    }

    return this.#httpClient
      .get<CoreClientApiUserExistsEmailResult>(
        `${
          environment.apiBaseUrl
        }/core-client/api/user/existsEmail?username=${encodeURIComponent(
          data.value,
        )}`,
        {},
      )
      .pipe(
        map((result) => {
          // case of exist
          if (result.exists) {
            if (data.attr === 'email') {
              throw new DuplicateEmailFailure();
            }

            if (data.attr === 'phone' || data.attr === 'phone_number') {
              throw new DuplicatePhoneNumberFailure();
            }

            throw new DuplicateUsernameFailure();
          }

          // case of invalid
          if (
            result.phoneType === 'INVALID' ||
            result.validPhoneNumber === false
          ) {
            throw new InvalidPhoneNumberFailure();
          }

          return {
            isValid: true,
          };
        }),
        catchError((error) => {
          if (error instanceof HttpResponseBase) {
            return throwError(() =>
              FzAuthFailures.internal(
                `Validating username ${data.value} failed with status ${error.status}`,
              ),
            );
          }
          return throwError(() => error);
        }),
      );
  }

  redirectToLogin() {
    this.redirectTo('/auth/sign-in');
  }

  redirectTo(path: string) {
    const params = new URLSearchParams(window.location.search);
    const inviteBy = params.get('inviteBy') || '';
    const joinUrl = localStorage.getItem('joinUrl') || '';
    if (inviteBy !== '' && joinUrl !== '') {
      window.location.href = joinUrl;
    } else {
      const redirectUrl = new URL(window.location.href);
      const divider = path.charAt(0) === '/' ? '' : '/';
      const basePath = new URL(`${window.location.origin}${divider}${path}`);

      // add redirect to query string
      basePath.searchParams.append('redirectTo', redirectUrl.toString());

      if (this.invite || basePath.searchParams.get('inviteBy')) {
        // add invite to login query string
        basePath.searchParams.append(
          'sponsor',
          this.invite || (basePath.searchParams.get('inviteBy') as string),
        );
      }

      const url = basePath.toString();
      window.location.href = url;
    }
  }

  setInvite(invite: string) {
    this.invite = invite;
  }

  mfaNotification(
    deviceKey: string,
    mfaPreference: UpdateMFAPreferenceInput,
  ): Observable<{ message: string }> {
    return this.#httpClient.post<{ message: string }>(
      `${environment.apiBaseUrl}/core-client/api/user/mfa-notification`,
      { deviceKey, mfaPreference },
    );
  }

  getCognitoTokens() {
    const lastAuthUser = localStorage.getItem(
      `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.LastAuthUser`,
    );

    if (lastAuthUser) {
      return {
        idToken: localStorage.getItem(
          `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${lastAuthUser}.idToken`,
        ),
        accessToken: localStorage.getItem(
          `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${lastAuthUser}.accessToken`,
        ),
        refreshToken: localStorage.getItem(
          `CognitoIdentityServiceProvider.${environment.Auth.userPoolWebClientId}.${lastAuthUser}.refreshToken`,
        ),
      };
    }

    return {};
  }

  getAuthenticationToken(): Observable<string | undefined> {
    const tokens = this.getCognitoTokens();

    if (Object.keys(tokens).length === 0) {
      return of(undefined);
    }

    if (environment.cognitoAdvancedSecurity === true) {
      return of(tokens['accessToken']!);
    }

    return of(tokens['idToken']!);
  }

  getAuthenticationTokenFromSession(): Observable<string> {
    return this.getAuthSession().pipe(
      map((session) => {
        if (!session.tokens?.idToken) {
          return '';
        }

        if (environment.cognitoAdvancedSecurity === true) {
          return session.tokens?.accessToken?.toString() || '';
        }

        return session.tokens?.idToken?.toString() || '';
      }),
    );
  }
}
